import { ReactElement, useEffect, useState } from "react";
import { getUrl } from "aws-amplify/storage";
import "./ConversationMessageContent.scss";
import SafeMarkdown from "../SafeMarkdown";
import classNames from "classnames";

// In `html` format, we sometimes embed a file object key in the message content like so:
// <div class="dynamic-embedded-image" fileobjectkey="..."></div>
// This function parses these tags and replaces it with an image tag that uses the
// resolved URL from `getUrl` for the file object key.
const parseDynamicEmbeddedImages = async (content: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const dynamicEmbeddedImages = doc.querySelectorAll(".dynamic-embedded-image");
  for (const el of Array.from(dynamicEmbeddedImages)) {
    const fileObjectPath = el.getAttribute("fileobjectkey");
    if (fileObjectPath) {
      const url = await getUrl({ path: fileObjectPath });
      const img = document.createElement("img");
      img.src = url.url.toString();
      el.replaceWith(img);
    }
  }
  return doc.body.innerHTML;
};

// In `html` format, a link sometimes is pasted as plain text in a `p` tag.
// We ought to convert it to an anchor tag with the same href.
const parseDynamicEmbeddedLinks = async (content: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const dynamicEmbeddedLinks = doc.querySelectorAll("p");
  for (const el of Array.from(dynamicEmbeddedLinks)) {
    const url = el.textContent;
    if (url && url.startsWith("http")) {
      const a = document.createElement("a");
      a.href = url;
      a.textContent = url;
      a.target = "_blank";
      el.replaceWith(a);
    }
  }
  return doc.body.innerHTML;
};

const ConversationMessageContent = ({
  messageContent,
  messageFormat,
  contentColor,
  preview,
}: {
  messageContent: string;
  messageFormat: "markdown" | "plain" | "html";
  contentColor?: "light" | "normal";
  preview?: React.ReactNode;
}) => {
  const [content, setContent] = useState(messageContent);

  useEffect(() => {
    if (messageFormat === "html") {
      parseDynamicEmbeddedImages(messageContent)
        .then(parseDynamicEmbeddedLinks)
        .then(setContent);
    } else if (messageFormat === "plain") {
      setContent(messageContent);
    }
  }, [messageContent, messageFormat, preview]);

  if (preview) {
    return preview;
  }

  if (!content.trim().length) {
    return null;
  }

  const urlPattern =
    /(http|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:/~+#-]*[\w@?^=%&amp;/~+#-])?/gi;

  const linkify = (text: string): ReactElement[] => {
    const elements: ReactElement[] = [];
    let lastIndex = 0;

    // Use matchAll to find all matches of URLs
    const matches = Array.from(text.matchAll(urlPattern));

    for (const match of matches) {
      const url = match[0];
      const offset = match.index || 0;

      // Push the non-URL text between the last match and this match, wrapped in a <span>
      if (offset > lastIndex) {
        elements.push(
          <span key={lastIndex}>{text.substring(lastIndex, offset)}</span>,
        );
      }

      // Add the URL as an <a> element
      const href = url.match(/^(https?:\/\/|www\.)/) ? url : `https://${url}`;
      elements.push(
        <a
          key={offset}
          href={href}
          className="tw-font-semibold tw-text-fay-500"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {url}
        </a>,
      );

      // Update lastIndex to the end of the current URL
      lastIndex = offset + url.length;
    }

    // Add any remaining non-URL text after the last match, wrapped in a <span>
    if (lastIndex < text.length) {
      elements.push(<span key={lastIndex}>{text.substring(lastIndex)}</span>);
    }

    return elements;
  };

  return (
    <>
      {messageFormat === "plain" && (
        <div className={styles.content(messageFormat, contentColor)}>
          {linkify(content)}
        </div>
      )}
      {messageFormat === "markdown" && (
        <SafeMarkdown
          className={styles.content(messageFormat, contentColor)}
          content={content}
        />
      )}
      {messageFormat === "html" && (
        <div
          className="html-set-message"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
    </>
  );
};

const styles = {
  content: (
    format: "markdown" | "plain" | "html",
    contentColor?: "light" | "normal",
  ) =>
    classNames("tw-font-medium", {
      "tw-text-neutral-400": contentColor === "light",
      "tw-whitespace-pre-wrap": format === "plain",
    }),
};

export default ConversationMessageContent;
